import { getAuth } from 'firebase/auth';
import { USERS_API_BASE_URL } from '../config/api';
import type { SavedCountryCode } from '../config/catalogue';

const BASE_URL = USERS_API_BASE_URL;

// Every create and update names the country, whatever the area type. The users API stamps NOR
// on a grid or admin save that leaves it out, which is how Estonian areas used to turn Norwegian.
export interface SaveGeometriesRequest {
  name: string;
  description: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code: SavedCountryCode;
}

/** A PUT replaces the saved fields with exactly these. */
export interface UpdateGeometriesRequest {
  name: string;
  description: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code: SavedCountryCode;
}

export interface SaveGeometriesResponse {
  msg: string;
  id: string;
  name: string;
  description: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code?: string | null;
}

export interface LoadGeometriesResponse {
  maps: {
    id: string;
    name: string;
    description: string;
    type: string;
    ids?: string[];
    geometry?: string;
    country_code?: string | null;
    // Realtime monitoring flag as stored in Firestore. UI state only — the
    // sampler keys off BigQuery `is_exported`, which can drift from this.
    is_monitored?: boolean;
    // The GCP project whose BigQuery holds this area's realtime data. Firestore
    // is shared across envs; realtime data is split. Absent on maps created
    // before the #425 stamp (backfilled by backfill-bqprojectid.mjs). Used by
    // src/utils/realtimeEnv.ts to grey out cross-env areas in realtime UIs.
    bqProjectId?: string;
  }[];
}

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };
  
  if (user) {
    try {
      const idToken = await user.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;
    } catch (error) {
      console.error('Failed to get Firebase ID token:', error);
      throw new Error('Authentication required - please log in');
    }
  } else {
    throw new Error('User not authenticated - please log in');
  }
  
  return headers;
};

export const userMapsApi = {
  async saveGeometries(request: SaveGeometriesRequest): Promise<SaveGeometriesResponse> {
    const url = `${BASE_URL}/user-data/maps`;
    console.log('🌐 Request URL:', url);
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });
      
      console.log('📡 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
      }
      
      const result = await response.json();
      return result;
      
    } catch (error) {
      console.error('Failed to save geometries:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Network error - check CORS and API availability: ${error.message}`);
      }
      throw error;
    }
  },

  async loadGeometries(): Promise<LoadGeometriesResponse> {
    const url = `${BASE_URL}/user-data/maps`;
    console.log('🌐 Request URL:', url);
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      
      console.log('📡 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
      }
      
      const result = await response.json();
      return result;
      
    } catch (error) {
      console.error('Failed to load geometries:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Network error - check CORS and API availability: ${error.message}`);
      }
      throw error;
    }
  },

  async deleteGeometries(id: string): Promise<{ msg: string }> {
    console.log('🗑️ Deleting user geometries:', id);
    
    const url = `${BASE_URL}/user-data/maps/${id}`;
    console.log('🌐 Request URL:', url);
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });
      
      console.log('📡 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
      }
      
      const result = await response.json();
      return result;
      
    } catch (error) {
      console.error('Failed to delete geometries:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Network error - check CORS and API availability: ${error.message}`);
      }
      throw error;
    }
  },

  async updateGeometries(id: string, request: UpdateGeometriesRequest): Promise<SaveGeometriesResponse> {
    const url = `${BASE_URL}/user-data/maps/${id}`;
    console.log('🌐 Request URL:', url);
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(request)
      });
      
      console.log('📡 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
      }
      
      const result = await response.json();
      return result;
      
    } catch (error) {
      console.error('Failed to update geometries:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Network error - check CORS and API availability: ${error.message}`);
      }
      throw error;
    }
  }
};